<template>
  <v-dialog
    v-model="dialog"
    :max-width="aiDialogMaxWidth"
    v-bind="modalP2FormViewport"
    :data-testid="getTestId('ai-work-dialog')"
    @click:outside="closeDialog"
    @close="closeDialog"
    @keydown.esc="closeDialog"
  >
    <v-card class="pa-0 ma-0">
      <CrudFormDialogHeader
        class="ai-work-dialog-header"
        :kicker="$t('aiJobTable.title')"
        :title="$t('aiJob.dialogTitle')"
        show-close
        @close="closeDialog"
      >
        <template #append>
          <div class="ai-work-dialog-header__actions d-flex align-center flex-wrap justify-end flex-grow-1">
            <div
              class="solvit-modal-header-context ai-work-dialog-header__context d-flex align-center flex-wrap justify-end"
            >
              <span v-if="reportInfo?.siteName" class="d-inline-flex align-center">
                <v-icon x-small class="mr-1 solvit-modal-header-context__icon"
                  >mdi-map-marker</v-icon
                >
                <span>{{ reportInfo.siteName }}</span>
              </span>
              <span v-if="reportInfo?.examineDate" class="d-inline-flex align-center">
                <v-icon x-small class="mr-1 solvit-modal-header-context__icon">mdi-calendar</v-icon>
                <span>{{ reportInfo.examineDate }}</span>
              </span>
              <span v-if="reportInfo?.reportName" class="d-inline-flex align-center">
                <v-icon x-small class="mr-1 solvit-modal-header-context__icon"
                  >mdi-file-document-outline</v-icon
                >
                <span
                  class="solvit-modal-header-context__primary text-truncate ai-work-dialog-header__report-name"
                  >{{ reportInfo.reportName }}</span
                >
              </span>
            </div>
            <v-checkbox
              v-model="isSamplingMode"
              :label="$t('aiJob.samplingMode')"
              hide-details
              class="ma-0 flex-shrink-0 ai-work-dialog-header__sampling"
              :data-testid="getTestId('sampling-mode-checkbox')"
            />
          </div>
        </template>
      </CrudFormDialogHeader>
      <v-card-text>
        <!-- RGB 작업 실행 섹션 -->
        <section class="mb-3">
          <div class="d-flex align-center mb-3 font-weight-medium text-subtitle-1">
            <v-icon class="mr-2" color="primary">mdi-palette</v-icon>
            <span>{{ $t('aiJob.rgbSection') }}</span>
          </div>
          <v-card outlined>
            <v-card-text class="py-3 pb-0">
              <div class="mb-3">
                <div class="d-flex align-center justify-space-between">
                  <div class="d-flex align-center mb-2">
                    <v-checkbox
                      v-model="selectedRgbJobKinds"
                      value="RGB_LOC"
                      :label="$t('aiJob.geoLocalization')"
                      :disabled="hasRunningJobsForKind('RGB_LOC') || isRequestingRgb"
                      hide-details
                      class="ma-0"
                    /><v-chip
                      v-if="hasRunningJobsForKind('RGB_LOC')"
                      small
                      color="info"
                      text-color="text"
                      class="ml-2"
                    >
                      {{ $t('ai.status.inProgress') }}
                    </v-chip>
                  </div>
                  <div class="d-flex align-center flex-wrap">
                    <altitude-input-fields
                      :base-altitude="baseRGBAltitude"
                      :relative-height-adjustment="rgbAltCorrectValue"
                      :altitude-data="rgbAltitudeData"
                      :camera-pitch="rgbCameraPitch"
                      @update:relativeHeightAdjustment="handleRGBRelativeHeightChange"
                      @update:cameraPitch="handleRgbCameraPitchInput"
                    />
                  </div>
                </div>
                <running-job-item-list
                  v-if="getRunningJobsForKind('RGB_LOC').length > 0"
                  :jobs="getRunningJobsForKind('RGB_LOC')"
                  :cancelling-job-id="cancellingJobId"
                  :get-job-sequence="getJobSequence"
                  :format-job-kinds="formatJobKinds"
                  :format-progress="formatProgress"
                  :get-status-color="getStatusColor"
                  :get-status-text="getStatusText"
                  :show-cancel-button="false"
                  :show-job-status="false"
                />
              </div>
            </v-card-text>
            <v-card-actions class="px-4 pb-3 pt-0">
              <v-spacer />
              <v-btn
                small
                outlined
                class="solvit-crud-outline-btn solvit-crud-outline-btn--primary"
                :disabled="hasRunningRgbJob || selectedRgbJobKinds.length === 0 || isRequestingRgb"
                :loading="isRequestingRgb"
                @click="requestRgbJob"
              >
                <v-icon left small>mdi-play</v-icon>
                {{ isSamplingMode ? $t('aiJob.runRgbSampling') : $t('aiJob.runRgb') }}
              </v-btn>
            </v-card-actions>
          </v-card>
        </section>

        <!-- Thermal 작업 실행 섹션 -->
        <section class="mb-3">
          <div class="d-flex align-center mb-3 font-weight-medium text-subtitle-1">
            <v-icon class="mr-2" color="primary">mdi-fire</v-icon>
            <span>{{ $t('aiJob.thermalSection') }}</span>
          </div>
          <v-card outlined>
            <v-card-text class="py-3 pb-0">
              <div class="mb-3">
                <div class="d-flex align-center justify-space-between">
                  <div class="d-flex align-center mb-2">
                    <v-checkbox
                      v-model="selectedThermalJobKinds"
                      value="IR_LOC"
                      :label="$t('aiJob.geoLocalization')"
                      :disabled="hasRunningJobsForKind('IR_LOC') || isRequestingThermal"
                      hide-details
                      class="ma-0"
                    />
                    <v-chip
                      v-if="hasRunningJobsForKind('IR_LOC')"
                      small
                      color="info"
                      text-color="text"
                      class="ml-2"
                    >
                      {{ $t('ai.status.inProgress') }}
                    </v-chip>
                  </div>
                  <div class="d-flex align-center flex-wrap">
                    <altitude-input-fields
                      :base-altitude="baseThermalAltitude"
                      :relative-height-adjustment="irAltCorrectValue"
                      :altitude-data="thermalAltitudeData"
                      :camera-pitch="thermalCameraPitch"
                      @update:relativeHeightAdjustment="handleThermalRelativeHeightChange"
                      @update:cameraPitch="handleThermalCameraPitchInput"
                    />
                  </div>
                </div>
                <running-job-item-list
                  v-if="getRunningJobsForKind('IR_LOC').length > 0"
                  :jobs="getRunningJobsForKind('IR_LOC')"
                  :cancelling-job-id="cancellingJobId"
                  :get-job-sequence="getJobSequence"
                  :format-job-kinds="formatJobKinds"
                  :format-progress="formatProgress"
                  :get-status-color="getStatusColor"
                  :get-status-text="getStatusText"
                  :show-cancel-button="false"
                  :show-job-status="false"
                />
              </div>

              <!-- Thermal 온도데이터 추출 -->
              <div class="mb-3">
                <div class="d-flex align-center mb-2">
                  <v-checkbox
                    v-model="selectedThermalJobKinds"
                    value="IR_DATA"
                    :label="$t('aiJob.temperatureExtract')"
                    :disabled="hasRunningJobsForKind('IR_DATA') || isRequestingThermal"
                    hide-details
                    class="ma-0"
                  />
                  <v-chip
                    v-if="hasRunningJobsForKind('IR_DATA')"
                    small
                    color="info"
                    text-color="text"
                    class="ml-2"
                  >
                    {{ $t('ai.status.inProgress') }}
                  </v-chip>
                </div>
                <running-job-item-list
                  v-if="getRunningJobsForKind('IR_DATA').length > 0"
                  :jobs="getRunningJobsForKind('IR_DATA')"
                  :cancelling-job-id="cancellingJobId"
                  :get-job-sequence="getJobSequence"
                  :format-job-kinds="formatJobKinds"
                  :format-progress="formatProgress"
                  :get-status-color="getStatusColor"
                  :get-status-text="getStatusText"
                  :show-cancel-button="false"
                  :show-job-status="false"
                />
              </div>

              <!-- Thermal AI 결함 검출 -->
              <div class="mb-3">
                <div class="d-flex align-center mb-2 flex-wrap">
                  <v-checkbox
                    v-model="selectedThermalJobKinds"
                    value="IR_DEFECT"
                    :label="$t('aiJob.aiDefectDetect')"
                    :disabled="hasRunningJobsForKind('IR_DEFECT') || isRequestingThermal"
                    hide-details
                    class="ma-0"
                  />
                  <v-chip
                    v-if="hasRunningJobsForKind('IR_DEFECT')"
                    small
                    color="info"
                    text-color="text"
                    class="ml-2"
                  >
                    {{ $t('ai.status.inProgress') }}
                  </v-chip>
                  <div
                    v-if="selectedThermalJobKinds.includes('IR_DEFECT')"
                    class="ai-detect-width-input ml-3 d-inline-flex align-center flex-shrink-0"
                    data-testid="ai-detect-width-percent-input"
                  >
                    <span class="text-body-2 mr-2 text--secondary">
                      {{ $t('aiJob.detectImageRatioLabel') }}
                    </span>
                    <v-text-field
                      :value="aiDetectWidthPercentForInput"
                      type="number"
                      dense
                      hide-details
                      single-line
                      min="4"
                      max="100"
                      step="0.1"
                      class="ai-detect-width-input__field ma-0 pt-0"
                      @input="onAiDetectWidthPercentInput"
                    />
                    <span class="text-body-2 ml-1">%</span>
                    <span class="text-body-2 text--secondary ml-2 ai-detect-width-input__hint">
                      {{ $t('aiJob.detectImageRatioHint') }}
                    </span>
                  </div>
                </div>
                <running-job-item-list
                  v-if="getRunningJobsForKind('IR_DEFECT').length > 0"
                  :jobs="getRunningJobsForKind('IR_DEFECT')"
                  :cancelling-job-id="cancellingJobId"
                  :get-job-sequence="getJobSequence"
                  :format-job-kinds="formatJobKinds"
                  :format-progress="formatProgress"
                  :get-status-color="getStatusColor"
                  :get-status-text="getStatusText"
                  :show-cancel-button="false"
                  :show-job-status="false"
                />
                <div
                  v-if="selectedThermalJobKinds.includes('IR_DEFECT')"
                  class="mt-3"
                  data-testid="ai-detect-region-picker-section"
                >
                  <div class="ai-detect-region-strip" data-testid="ai-detect-region-strip">
                    <div class="ai-detect-region-frame" data-testid="ai-detect-region-prev">
                      <v-skeleton-loader
                        v-if="thermalPreviewLoading"
                        type="image"
                        class="ai-detect-region-frame__skeleton"
                        :width="aiDetectPreviewMaxPx"
                        :height="aiDetectPreviewSkeletonHeight"
                      />
                      <button
                        v-else-if="thermalPreviewPrevImageUrl"
                        type="button"
                        class="ai-detect-region-frame__btn ai-detect-region-frame__btn--picker"
                        @click="shiftThermalPreview(-1)"
                      >
                        <IrAiDetectRegionPicker
                          :image-url="thermalPreviewPrevImageUrl"
                          :value="aiDetectImageLinearRatio"
                          :display-max-px="aiDetectPreviewMaxPx"
                          guide-only
                          :show-width-label="false"
                        />
                      </button>
                      <div
                        v-else
                        class="ai-detect-region-frame__btn ai-detect-region-frame__btn--empty"
                      />
                    </div>
                    <div class="ai-detect-region-frame ai-detect-region-frame--center">
                      <v-skeleton-loader
                        v-if="thermalPreviewLoading"
                        type="image"
                        class="ai-detect-region-frame__skeleton"
                        :width="aiDetectPreviewMaxPx"
                        :height="aiDetectPreviewSkeletonHeight"
                      />
                      <IrAiDetectRegionPicker
                        v-else
                        :image-url="thermalPreviewCurrentUrl"
                        :value="aiDetectImageLinearRatio"
                        :display-max-px="aiDetectPreviewMaxPx"
                        :show-width-label="false"
                        @input="onAiDetectImageRatioInput"
                      />
                    </div>
                    <div class="ai-detect-region-frame" data-testid="ai-detect-region-next">
                      <v-skeleton-loader
                        v-if="thermalPreviewLoading"
                        type="image"
                        class="ai-detect-region-frame__skeleton"
                        :width="aiDetectPreviewMaxPx"
                        :height="aiDetectPreviewSkeletonHeight"
                      />
                      <button
                        v-else-if="thermalPreviewNextImageUrl"
                        type="button"
                        class="ai-detect-region-frame__btn ai-detect-region-frame__btn--picker"
                        @click="shiftThermalPreview(1)"
                      >
                        <IrAiDetectRegionPicker
                          :image-url="thermalPreviewNextImageUrl"
                          :value="aiDetectImageLinearRatio"
                          :display-max-px="aiDetectPreviewMaxPx"
                          guide-only
                          :show-width-label="false"
                        />
                      </button>
                      <div
                        v-else
                        class="ai-detect-region-frame__btn ai-detect-region-frame__btn--empty"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </v-card-text>
            <v-card-actions class="px-4 pb-3 pt-0">
              <v-spacer />
              <v-btn
                small
                outlined
                class="solvit-crud-outline-btn solvit-crud-outline-btn--primary"
                :disabled="
                  hasRunningSelectedThermalJobs ||
                  selectedThermalJobKinds.length === 0 ||
                  isRequestingThermal
                "
                :loading="isRequestingThermal"
                @click="requestThermalJob"
              >
                <v-icon left small>mdi-play</v-icon>
                {{ isSamplingMode ? $t('aiJob.runThermalSampling') : $t('aiJob.runThermal') }}
              </v-btn>
            </v-card-actions>
          </v-card>
        </section>

        <!-- 실행 중인 작업 리스트 섹션 -->
        <section>
          <div class="d-flex align-center mb-3 font-weight-medium text-subtitle-1">
            <v-icon class="mr-2" color="primary">mdi-format-list-bulleted</v-icon>
            <span>{{ $t('aiJob.runningJobsSection') }}</span>
          </div>
          <v-card outlined>
            <running-job-item-list
              v-if="runningJobs.length > 0"
              :jobs="runningJobs"
              :cancelling-job-id="cancellingJobId"
              :get-job-sequence="getJobSequence"
              :format-job-kinds="formatJobKinds"
              :format-progress="formatProgress"
              :get-status-color="getStatusColor"
              :get-status-text="getStatusText"
              @cancel="cancelJob"
            />
            <v-card-text v-else class="text-center py-4 text--secondary">
              {{ $t('aiJob.noRunningJobs') }}
            </v-card-text>
          </v-card>
        </section>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
  import { AiAnalysisService } from '@/_services/aiAnalysis.service';
  import { ExamineOutputService } from '@/_services/examine.output.service';
  import { SolarExamineReportService } from '@/_services/solar.examine.report.service';
  import IrAiDetectRegionPicker from '@/components/common/IrAiDetectRegionPicker.vue';
  import {
    DEFAULT_AI_DEFECT_DISPLAY_LINEAR_RATIO,
    linearRatioFromWidthPercent,
    widthPercentFromLinearRatio
  } from '@/utils/defect/aiDefectDisplayRegion';
  import { AI_JOB_KINDS } from '@/constants/aiJobKinds';
  import { getErrorMessage, isRequestAborted, throwErrorIfFailed } from '@/utils/apiHelpers';
  import { mapActions } from 'vuex';
  import { getStatusColor, getStatusText } from '@/constants/progressStatus';
  import { formatJobKinds, normalizeJobKinds } from '@/utils/aiJobUtils';
  import pollingService from '@/utils/pollingService';
  import { normalizeReportInfo } from '@/utils/reportUtils';
  import { getTestId } from '@/utils/testUtils';
  import RunningJobItemList from './RunningJobItemList.vue';
  import altitudeInputFields from '../common/altitudeInputFields.vue';
  import CrudFormDialogHeader from '@/components/common/CrudFormDialogHeader.vue';
  import { MODAL_MAX_WIDTH, MODAL_P2_FORM_VIEWPORT } from '@/constants/modalLayout';

  export default {
    name: 'AIAnalyzeDialog',
    components: {
      RunningJobItemList,
      altitudeInputFields,
      CrudFormDialogHeader,
      IrAiDetectRegionPicker
    },
    props: {
      reportId: {
        type: Number,
        required: true
      },
      dialog: {
        type: Boolean,
        required: true
      },
      editedItem: {
        type: Object,
        required: false,
        default: () => ({
          type: null,
          solarExamineReport: null,
          targetImageCount: null,
          processStatus: 'BEF',
          processRate: null,
          jobRefSeq: null
        })
      }
    },
    data() {
      return {
        modalLayout: MODAL_MAX_WIDTH,
        modalP2FormViewport: MODAL_P2_FORM_VIEWPORT,
        selectedRgbJobKinds: [],
        isRequestingRgb: false,
        selectedThermalJobKinds: [],
        isRequestingThermal: false,
        isSamplingMode: false,
        baseRGBAltitude: 0,
        baseThermalAltitude: 0,
        rgbAltCorrectValue: 0,
        irAltCorrectValue: 0,
        aiJobs: [],
        cancellingJobId: null,
        rgbAltitudeData: null,
        thermalAltitudeData: null,
        rgbCameraPitch: -90,
        thermalCameraPitch: -90,
        aiDetectImageLinearRatio: DEFAULT_AI_DEFECT_DISPLAY_LINEAR_RATIO,
        thermalPreviewImages: [],
        thermalPreviewIndex: 0,
        thermalPreviewLoading: false,
        aiDetectPreviewMaxPx: 250,
        dialogAbortController: null
      };
    },
    computed: {
      reportInfo() {
        if (this.editedItem?.reportInfo) {
          return this.editedItem.reportInfo;
        }
        if (this.editedItem && (this.editedItem.name || this.editedItem.examineDate)) {
          return normalizeReportInfo(this.editedItem, 'table');
        }
        return null;
      },
      /** 보고서에 저장된 카메라 pitch 값 */
      reportCameraPitch() {
        const pitch = this.editedItem?.cameraPitch;
        if (pitch === null || pitch === undefined) return -90;
        return Number(pitch);
      },
      /** RGB AI 실행 시 서버로 전달할 높이 소스 */
      resolvedRgbHeightSource() {
        return Number(this.rgbCameraPitch) === -90 ? 'LRF' : 'GPS';
      },
      /** IR AI 실행 시 서버로 전달할 높이 소스 */
      resolvedThermalHeightSource() {
        return Number(this.thermalCameraPitch) === -90 ? 'LRF' : 'GPS';
      },
      aiDialogMaxWidth() {
        if (this.selectedThermalJobKinds.includes('IR_DEFECT')) {
          return '1040px';
        }
        return '880px';
      },
      aiDetectWidthPercentForInput() {
        return widthPercentFromLinearRatio(this.aiDetectImageLinearRatio);
      },
      aiDetectPreviewSkeletonHeight() {
        return Math.round((this.aiDetectPreviewMaxPx * 512) / 640);
      },
      thermalPreviewCurrentUrl() {
        return this.thermalPreviewImages[this.thermalPreviewIndex]?.imgUrl || '';
      },
      thermalPreviewPrevImageUrl() {
        const prev = this.thermalPreviewImages[this.thermalPreviewIndex - 1];
        return prev?.imgUrl || '';
      },
      thermalPreviewNextImageUrl() {
        const next = this.thermalPreviewImages[this.thermalPreviewIndex + 1];
        return next?.imgUrl || '';
      },
      finalAltitude() {
        const base = parseFloat(this.baseRGBAltitude) || 0;
        const adjustment = parseFloat(this.rgbAltCorrectValue) || 0;
        return (base + adjustment).toFixed(2);
      },
      hasRunningRgbJob() {
        return this.aiJobs.some(job => job.processStatus === 'ING' && job.resourceType === 'RGB');
      },
      hasRunningSelectedThermalJobs() {
        if (this.selectedThermalJobKinds.length === 0) {
          return false;
        }
        return this.selectedThermalJobKinds.some(jobKind => this.hasRunningJobsForKind(jobKind));
      },
      runningJobs() {
        return this.aiJobs.filter(job => job.processStatus === 'ING');
      }
    },
    watch: {
      dialog: {
        async handler(newValue) {
          if (newValue) {
            this.initDialogAbortController();
            this.selectedRgbJobKinds = [];
            this.selectedThermalJobKinds = [];
            this.isRequestingRgb = false;
            this.isRequestingThermal = false;
            this.isSamplingMode = false;
            this.aiDetectImageLinearRatio = DEFAULT_AI_DEFECT_DISPLAY_LINEAR_RATIO;
            this.rgbCameraPitch = this.reportCameraPitch;
            this.thermalCameraPitch = this.reportCameraPitch;
            await Promise.all([this.loadAiJobs(), this.loadAltitudeData()]);
            await this.startProgressPollingIfNeeded();
          } else {
            this.cancelPendingDialogRequests();
            this.stopProgressPolling();
          }
        },
        immediate: true
      }
    },
    beforeDestroy() {
      this.cancelPendingDialogRequests();
      this.stopProgressPolling();
    },
    methods: {
      ...mapActions('commonAlert', ['addAlert']),

      getTestId(testId) {
        return getTestId(testId);
      },

      initDialogAbortController() {
        if (this.dialogAbortController) {
          this.dialogAbortController.abort();
        }
        this.dialogAbortController = new AbortController();
      },

      getDialogRequestConfig() {
        const signal = this.dialogAbortController?.signal;
        return signal ? { signal } : undefined;
      },

      cancelPendingDialogRequests() {
        if (this.dialogAbortController) {
          this.dialogAbortController.abort();
          this.dialogAbortController = null;
        }
        this.isRequestingRgb = false;
        this.isRequestingThermal = false;
        this.thermalPreviewLoading = false;
        this.cancellingJobId = null;
      },

      async startProgressPollingIfNeeded() {
        pollingService.startPolling(
          async () => {
            return this.loadAiJobs();
          },
          () => {},
          () => true,
          3000
        );
      },

      hasRunningJob() {
        return this.aiJobs.some(job => job.processStatus === 'ING');
      },

      async stopProgressPolling() {
        pollingService.stopPolling();
      },

      async loadAiJobs() {
        try {
          const jobs = await AiAnalysisService.getAiAnalysisJobs(
            this.reportId,
            this.getDialogRequestConfig()
          );
          this.aiJobs = jobs;
        } catch (error) {
          if (isRequestAborted(error)) {
            return;
          }
          console.error('AI Job 리스트 조회 실패:', error);
          this.aiJobs = [];
        }
      },

      resolveAltitudeApiErrorMessage(error, response) {
        if (error?.resultDesc) {
          return error.resultDesc;
        }
        if (response) {
          return getErrorMessage(response);
        }
        return error?.message || this.$t('aiJob.altitudeLoadFailed');
      },

      notifyAltitudeLoadFailure(sectionLabel, error, response) {
        const message = this.resolveAltitudeApiErrorMessage(error, response);
        this.addAlert({
          type: 'warning',
          message: `${sectionLabel}: ${message}`,
          timeout: 8000
        });
      },

      async loadAltitudeData() {
        const requestConfig = this.getDialogRequestConfig();
        await Promise.all([
          this.loadAltitudeDataForSection(
            'rgb',
            () => AiAnalysisService.getRgbImageAltitudeData(this.reportId, requestConfig),
            'rgbAltitudeData'
          ),
          this.loadAltitudeDataForSection(
            'thermal',
            () => AiAnalysisService.getThermalImageAltitudeData(this.reportId, requestConfig),
            'thermalAltitudeData'
          ),
          this.loadThermalPreviewImage()
        ]);
      },

      async loadAltitudeDataForSection(sectionKey, fetcher, stateKey) {
        const sectionLabel =
          sectionKey === 'rgb' ? this.$t('aiJob.rgbSection') : this.$t('aiJob.thermalSection');
        try {
          this[stateKey] = await fetcher();
        } catch (error) {
          if (isRequestAborted(error)) {
            return;
          }
          console.warn(`[AIAnalyzeDialog] ${sectionKey} altitude load failed`, error);
          this[stateKey] = null;
          this.notifyAltitudeLoadFailure(sectionLabel, error, error?.response);
        }
      },

      resolveDefaultThermalPreviewIndex(images) {
        if (!Array.isArray(images) || images.length < 2) {
          return 0;
        }
        return 1;
      },

      async loadThermalPreviewImage() {
        this.thermalPreviewLoading = true;
        try {
          const data = await ExamineOutputService.getThermalImages(
            this.reportId,
            false,
            this.getDialogRequestConfig()
          );
          const list = data?.content || (Array.isArray(data) ? data : []);
          this.thermalPreviewImages = list.filter(img => img?.imgUrl);
          this.thermalPreviewIndex = this.resolveDefaultThermalPreviewIndex(
            this.thermalPreviewImages
          );
        } catch (error) {
          if (isRequestAborted(error)) {
            return;
          }
          console.warn('열화상 미리보기 로드 실패:', error);
          this.thermalPreviewImages = [];
          this.thermalPreviewIndex = 0;
        } finally {
          this.thermalPreviewLoading = false;
        }
      },

      shiftThermalPreview(delta) {
        const next = this.thermalPreviewIndex + delta;
        if (next < 0 || next >= this.thermalPreviewImages.length) {
          return;
        }
        this.thermalPreviewIndex = next;
      },

      onAiDetectImageRatioInput(linearRatio) {
        this.aiDetectImageLinearRatio = linearRatio;
      },

      onAiDetectWidthPercentInput(raw) {
        if (raw === '' || raw == null) {
          this.aiDetectImageLinearRatio = null;
          return;
        }
        this.aiDetectImageLinearRatio = linearRatioFromWidthPercent(raw);
      },

      resolveAiDetectImageRatioForRequest(normalizedJobKinds) {
        if (!normalizedJobKinds.includes(AI_JOB_KINDS.IR_DEFECT)) {
          return null;
        }
        const r = this.aiDetectImageLinearRatio;
        if (r == null || r >= 0.997) {
          return null;
        }
        return r;
      },

      getJobSequence(jobId) {
        const runningJobs = this.runningJobs;
        const index = runningJobs.findIndex(job => job.id === jobId);
        return index >= 0 ? index + 1 : 0;
      },

      getRunningJobsForKind(jobKind) {
        const normalizedKind = jobKind.toUpperCase();
        return this.aiJobs.filter(job => {
          if (job.processStatus !== 'ING') {
            return false;
          }
          const jobKinds = normalizeJobKinds(job.jobKinds);
          return jobKinds.includes(normalizedKind);
        });
      },

      hasRunningJobsForKind(jobKind) {
        return this.getRunningJobsForKind(jobKind).length > 0;
      },

      handleRGBRelativeHeightChange(value) {
        const numValue = parseFloat(value) || 0;
        this.rgbAltCorrectValue = numValue;
      },

      handleThermalRelativeHeightChange(value) {
        const numValue = parseFloat(value) || 0;
        this.irAltCorrectValue = numValue;
      },

      clampCameraPitch(value) {
        const n = Number(value);
        if (!Number.isFinite(n)) {
          return -90;
        }
        return Math.max(-90, Math.min(90, Math.round(n)));
      },

      handleRgbCameraPitchInput(value) {
        this.rgbCameraPitch = this.clampCameraPitch(value);
      },

      handleRgbCameraPitchBlur(event) {
        this.rgbCameraPitch = this.clampCameraPitch(event?.target?.value);
      },

      handleThermalCameraPitchInput(value) {
        this.thermalCameraPitch = this.clampCameraPitch(value);
      },

      handleThermalCameraPitchBlur(event) {
        this.thermalCameraPitch = this.clampCameraPitch(event?.target?.value);
      },

      async requestRgbJob() {
        if (this.selectedRgbJobKinds.length === 0) {
          alert(this.$t('aiJob.selectRgbJob'));
          return;
        }

        if (this.hasRunningRgbJob) {
          alert(this.$t('aiJob.rgbJobAlreadyRunning'));
          return;
        }

        try {
          this.isRequestingRgb = true;
          const normalized = normalizeJobKinds(this.selectedRgbJobKinds);
          const altCorrectValue = parseFloat(this.rgbAltCorrectValue) || 0;
          const response = await SolarExamineReportService.requestAIAnalysisJob(
            this.reportId,
            normalized,
            altCorrectValue,
            this.isSamplingMode,
            null,
            this.resolvedRgbHeightSource
          );

          throwErrorIfFailed(response);

          this.selectedRgbJobKinds = [];
          await this.loadAiJobs();
        } catch (error) {
          if (isRequestAborted(error)) {
            return;
          }
          console.error('RGB 작업 실행 요청 실패:', error);
          alert(this.$t('aiJob.rgbRequestError'));
        } finally {
          this.isRequestingRgb = false;
        }
      },

      async requestThermalJob() {
        if (this.selectedThermalJobKinds.length === 0) {
          alert(this.$t('aiJob.selectThermalJob'));
          return;
        }

        if (this.hasRunningSelectedThermalJobs) {
          alert(this.$t('aiJob.thermalJobConflict'));
          return;
        }

        try {
          this.isRequestingThermal = true;
          const normalized = normalizeJobKinds(this.selectedThermalJobKinds);
          const altCorrectValue = parseFloat(this.irAltCorrectValue) || 0;
          const response = await SolarExamineReportService.requestAIAnalysisJob(
            this.reportId,
            normalized,
            altCorrectValue,
            this.isSamplingMode,
            this.resolveAiDetectImageRatioForRequest(normalized),
            this.resolvedThermalHeightSource
          );

          throwErrorIfFailed(response);

          this.selectedThermalJobKinds = [];
          await this.loadAiJobs();
        } catch (error) {
          if (isRequestAborted(error)) {
            return;
          }
          console.error('열화상 작업 실행 요청 실패:', error);
          alert(this.$t('aiJob.thermalRequestError'));
        } finally {
          this.isRequestingThermal = false;
        }
      },

      async cancelJob(aiJobId) {
        if (!confirm(this.$t('aiJob.confirmCancelJob'))) {
          return;
        }

        try {
          this.cancellingJobId = aiJobId;
          await AiAnalysisService.cancelAiAnalysisJob(aiJobId);

          await this.loadAiJobs();

          alert(this.$t('aiJob.jobCancelDone'));
        } catch (error) {
          if (isRequestAborted(error)) {
            return;
          }
          console.error('작업 취소 실패:', error);
          alert(this.$t('aiJob.jobCancelError'));
        } finally {
          this.cancellingJobId = null;
        }
      },

      formatJobKinds,
      formatProgress(job) {
        const rate = job.processRate || 0;
        const percentage = Math.round(rate * 100);
        const processed = `${job.completeImageCount || 0}/${job.targetImageCount || 0}`;
        return this.$t('aiJob.progressFormat', { processed, percentage });
      },

      getStatusText,
      getStatusColor,

      closeDialog() {
        this.cancelPendingDialogRequests();
        this.stopProgressPolling();
        this.$emit('close');
      }
    }
  };
</script>

<style scoped>
  /* 헤더: 보고서 정보 ↔ 샘플링 ↔ 닫기(X) 간격 */
  .ai-work-dialog-header ::v-deep .solvit-dialog-form-header__row {
    column-gap: 16px;
    padding-left: 16px !important;
    padding-right: 16px !important;
  }

  .ai-work-dialog-header ::v-deep .solvit-dialog-form-header__append {
    align-items: center;
    gap: 12px 20px;
  }

  .ai-work-dialog-header ::v-deep .solvit-dialog-form-header__close {
    margin-left: 4px;
  }

  .ai-work-dialog-header__actions {
    gap: 20px;
    min-width: 0;
  }

  .ai-work-dialog-header__context {
    gap: 12px 16px;
  }

  .ai-work-dialog-header__report-name {
    max-width: 16rem;
  }

  .ai-work-dialog-header__sampling {
    padding-left: 4px;
  }

  .ai-job-pitch-input__field {
    width: 120px;
  }

  .ai-detect-region-strip {
    --ai-detect-preview-w: 250px;
    --ai-detect-preview-h: 200px;
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
    justify-content: center;
    align-items: flex-start;
    gap: 10px;
    padding-top: 2px;
    padding-bottom: 2px;
    width: 100%;
    overflow-x: auto;
  }

  .ai-detect-region-frame {
    flex: 0 0 var(--ai-detect-preview-w);
    width: var(--ai-detect-preview-w);
    height: var(--ai-detect-preview-h);
    max-width: var(--ai-detect-preview-w);
    max-height: var(--ai-detect-preview-h);
  }

  .ai-detect-region-frame--center {
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  .ai-detect-region-frame__btn {
    display: block;
    width: var(--ai-detect-preview-w);
    height: var(--ai-detect-preview-h);
    max-width: var(--ai-detect-preview-w);
    max-height: var(--ai-detect-preview-h);
    padding: 0;
    border: none;
    border-radius: 4px;
    background: rgb(0 0 0 / 88%);
    cursor: pointer;
    overflow: hidden;
  }

  .ai-detect-region-frame__btn--picker {
  }

  .ai-detect-region-frame__btn--empty {
    cursor: default;
    border: thin dashed rgb(127 127 127 / 40%);
  }

  .ai-detect-region-frame--center :deep(.ir-ai-detect-picker__svg) {
    border: 2px solid rgb(66 165 245 / 95%);
    box-shadow:
      0 0 0 2px rgb(66 165 245 / 22%),
      0 8px 20px rgb(0 0 0 / 36%);
  }

  .ai-detect-region-frame__btn:not(.ai-detect-region-frame__btn--empty):hover,
  .ai-detect-region-frame__btn--picker:not(.ai-detect-region-frame__btn--empty):hover {
    outline: 1px solid rgb(83 255 51 / 55%);
  }

  .ai-detect-region-frame__btn--picker {
    line-height: 0;
  }

  .ai-detect-region-frame--center ::v-deep .ir-ai-detect-picker__svg {
    max-height: var(--ai-detect-preview-h);
  }

  .ai-detect-width-input__field {
    max-width: 88px;
  }

  .ai-detect-width-input ::v-deep .v-input__slot {
    min-height: 32px !important;
  }

  .ai-detect-width-input ::v-deep input {
    text-align: right;
    font-weight: 500;
  }

  .ai-detect-width-input__hint {
    white-space: nowrap;
  }

  .ai-detect-region-frame__skeleton {
    flex-shrink: 0;
    border-radius: 4px;
    overflow: hidden;
  }

  .ai-detect-region-frame__skeleton ::v-deep .v-skeleton-loader__image {
    height: 100%;
  }
</style>
